import React, { useEffect } from "react";
import { ArrowRight, ChevronRight, Plus } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Ansprechpartner } from "@/components/site/Ansprechpartner";
import { Kontakt } from "@/components/site/Kontakt";
import { FlowLines } from "@/components/site/FlowLines";
import { Container, Label } from "@/components/site/Primitives";
import { Toaster } from "@/components/ui/sonner";
import content from "@/data/content.json";
import lexikon from "@/data/lexikon.json";

const { autor } = content;

const fmtDate = (iso) => new Date(iso).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });

export const anchorId = (text) =>
  text
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const Block = ({ b }) => {
  if (b.t === "h2")
    return (
      <h2 id={anchorId(b.c)} className="mt-12 scroll-mt-[110px] text-[24px] font-bold leading-[1.3] text-[#1e2532] md:text-[27px]">
        {b.c}
      </h2>
    );
  if (b.t === "ul")
    return (
      <ul className="mt-5 space-y-3">
        {b.items.map((it) => (
          <li key={it} className="flex gap-3 text-[16.5px] leading-[1.75] text-[#3c4356]">
            <span className="mt-[11px] h-[6px] w-[6px] shrink-0 rounded-full bg-[#0f9d6e]" />
            {it}
          </li>
        ))}
      </ul>
    );
  return <p className="mt-5 text-[16.5px] leading-[1.85] text-[#3c4356]">{b.c}</p>;
};

export default function RatgeberArticle({ data }) {
  useEffect(() => {
    document.title = data.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", data.metaDescription);
    window.scrollTo(0, 0);
  }, [data]);

  const toc = data.blocks.filter((b) => b.t === "h2").map((b) => b.c);
  const updated = data.updated || data.date;

  return (
    <>
      <Header />
      <main data-testid="article-main">
        <section className="has-lines relative overflow-hidden bg-[#1e2532] pb-16 pt-[150px] text-white on-dark">
          <FlowLines />
          <Container className="max-w-[900px]">
            <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1.5 text-[13px] text-white/50">
              <a href="/" className="hover:text-white">Startseite</a>
              <ChevronRight size={13} />
              <a href="/ratgeber/" className="hover:text-white">Ratgeber</a>
              <ChevronRight size={13} />
              <span className="text-white/80">{data.category}</span>
            </nav>
            <Label>{data.category}</Label>
            <h1 className="mt-7 font-bold" data-testid="article-h1" style={{ fontSize: "clamp(28px,4vw,44px)", lineHeight: 1.15 }}>
              {data.title}
            </h1>
            <p className="mt-6 text-[14px] text-white/55" data-testid="article-meta">
              {fmtDate(data.date)} · {data.readTime} Lesezeit · {data.author || autor.name}
            </p>
            <p className="mt-2 text-[13.5px] text-white/40" data-testid="article-updated">
              Zuletzt aktualisiert: {fmtDate(updated)}
            </p>
          </Container>
        </section>

        <article className="section-pad bg-white">
          <Container className="max-w-[820px]">
            <div className="img-frame mb-12 aspect-[16/9] w-full">
              <img src={data.image} alt={data.title} loading="eager" decoding="async" width="1264" height="711" />
            </div>

            {toc.length > 2 && (
              <nav data-testid="article-toc" aria-label="Inhaltsverzeichnis" className="rounded-[16px] border border-[#dfe2e0] bg-[#f8f9f8] p-7">
                <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-[#0b7d57]">Inhalt</p>
                <ol className="mt-5 space-y-2.5">
                  {toc.map((t, i) => (
                    <li key={t} className="flex gap-3 text-[15.5px] leading-[1.6]">
                      <span className="font-display font-bold text-[#0f9d6e]">{String(i + 1).padStart(2, "0")}</span>
                      <a href={`#${anchorId(t)}`} className="link-underline text-[#3c4356]">
                        {t}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            {data.blocks.map((b, i) => (
              <Block key={i} b={b} />
            ))}

            {data.faq && (
              <section data-testid="article-faq" className="mt-14">
                <h2 id="haeufige-fragen" className="scroll-mt-[110px] text-[24px] font-bold leading-[1.3] text-[#1e2532] md:text-[27px]">
                  Häufige Fragen
                </h2>
                <div className="mt-6 border-t border-[#dfe2e0]">
                  {data.faq.map((f, i) => (
                    <div key={f.q} className="border-b border-[#dfe2e0] py-6" data-testid={`article-faq-${i}`}>
                      <h3 className="flex items-start gap-3 text-[17px] font-semibold leading-[1.45] text-[#1e2532]">
                        <Plus size={18} className="mt-[3px] shrink-0 text-[#0f9d6e]" />
                        {f.q}
                      </h3>
                      <p className="mt-3 pl-[30px] text-[16px] leading-[1.8] text-[#565f70]">{f.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {data.lexikon && (
              <section data-testid="article-lexikon" className="mt-14">
                <h2 className="text-[24px] font-bold leading-[1.3] text-[#1e2532] md:text-[27px]">Verwandte Begriffe</h2>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {data.lexikon
                    .filter((s) => lexikon[s])
                    .map((s) => (
                      <a
                        key={s}
                        href={`/lexikon/${s}/`}
                        data-testid={`article-lexikon-${s}`}
                        className="rounded-full border border-[#d4d8d5] bg-[#f8f9f8] px-4 py-2 text-[14px] font-medium text-[#3c4356] hover:border-[#0f9d6e] hover:text-[#0b7d57]"
                        style={{ transition: "border-color .3s ease, color .3s ease" }}
                      >
                        {lexikon[s].term}
                      </a>
                    ))}
                </div>
              </section>
            )}

            {data.service && (
              <a
                href={data.service.href}
                data-testid="article-service"
                className="card-hover mt-14 flex flex-col gap-4 rounded-[16px] border border-[#dfe2e0] bg-white p-7 md:flex-row md:items-center md:justify-between md:p-8"
              >
                <div>
                  <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-[#0b7d57]">Passende Leistung</p>
                  <p className="mt-3 text-[19px] font-semibold text-[#1e2532]">{data.service.title}</p>
                  <p className="mt-2 text-[15.5px] leading-[1.7] text-[#565f70]">{data.service.text}</p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 text-[13px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
                  Mehr erfahren <ArrowRight size={16} />
                </span>
              </a>
            )}

            <aside data-testid="article-cta" className="mt-14 rounded-[16px] border border-[#0f9d6e]/25 bg-[#f2f8f5] p-8 md:p-10">
              <p className="text-[18px] font-semibold text-[#1e2532]">{data.cta.text}</p>
              <a
                href={data.cta.href}
                className="btn-solid mt-6 inline-flex items-center gap-2 px-6 py-3.5 text-[13px] font-bold uppercase tracking-[0.06em]"
              >
                <span>{data.cta.label}</span>
                <ArrowRight size={15} />
              </a>
            </aside>

            <aside data-testid="article-author" className="mt-12 rounded-[16px] border border-[#dfe2e0] bg-white p-7 md:p-8">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                <img
                  src={autor.image}
                  alt={autor.imageAlt}
                  loading="lazy"
                  decoding="async"
                  className="h-20 w-20 shrink-0 rounded-full object-cover object-top"
                />
                <div>
                  <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-[#0b7d57]">Über den Autor</p>
                  <p className="mt-3 text-[18px] font-semibold text-[#1e2532]">{data.author || autor.name}</p>
                  <p className="text-[14.5px] text-[#8a90a0]">{autor.role}</p>
                  <p className="mt-3 text-[15.5px] leading-[1.7] text-[#565f70]">{autor.short}</p>
                </div>
              </div>
            </aside>

            <p className="mt-10">
              <a href="/ratgeber/" className="inline-flex items-center gap-2 text-[14px] font-bold uppercase tracking-[0.08em] text-[#1e2532]">
                <span className="link-underline">Alle Ratgeber-Artikel</span>
                <ArrowRight size={15} />
              </a>
            </p>
          </Container>
        </article>

        <Ansprechpartner />
        <Kontakt />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </>
  );
}
